import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Box, AlertCircle, Ruler, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { getMpConnect } from "@/lib/matterport";

// Formats a distance in meters to metric or imperial display.
const fmtMeters = (meters, units) => {
  if (meters == null) return null;
  if (units === "imperial") {
    const totalIn = meters * 39.3701;
    const ft = Math.floor(totalIn / 12);
    const inch = Math.round(totalIn - ft * 12);
    return ft > 0 ? `${ft}' ${inch}"` : `${inch}"`;
  }
  return `${meters.toFixed(2)} m`;
};

// Severity → Matterport tag colour (RGB 0-1).
const SEV_RGB = {
  low: { r: 0.16, g: 0.73, b: 0.51 },
  medium: { r: 0.77, g: 0.64, b: 0.29 },
  high: { r: 0.92, g: 0.35, b: 0.05 },
  critical: { r: 0.86, g: 0.15, b: 0.15 },
};

// Embeds the Matterport scan, connects the Showcase SDK, tracks pointer
// intersection (for pinning), sweeps (for AI auto-scan) and measurements.
const MatterportViewer = forwardRef(({ modelId, sdkKey, onDebug }, ref) => {
  const dbg = (m) => { try { console.log("[PIN]", m); onDebug && onDebug(String(m)); } catch (e) {} };
  const loggedHit = useRef(false);
  const iframeRef = useRef(null);
  const sdkRef = useRef(null);
  const lastIntersect = useRef(null);
  const hitHistoryRef = useRef([]); // [{t, position, normal, floorId}] recent hover points
  const pinCaptureCancelRef = useRef(null);
  const sweepsRef = useRef([]);
  const measRef = useRef([]);
  const poseRef = useRef(null);
  const pinsRef = useRef({}); // defectId -> native tag sid
  const tagsRef = useRef([]); // live tag collection from Matterport
  const [status, setStatus] = useState("loading"); // loading | ready | error | noscan
  const [measuring, setMeasuring] = useState(false);
  const [units, setUnits] = useState("metric"); // metric | imperial
  const unitsRef = useRef("metric");

  useImperativeHandle(ref, () => ({
    isReady: () => status === "ready",
    getPlacement: () => {
      const p = lastIntersect.current;
      if (p && p.position) return { position: p.position, normal: p.normal };
      return { position: null, normal: null };
    },
    // Latest 3D world point the cursor is over (used to place a native pin).
    getPointerHit: () => {
      const p = lastIntersect.current;
      if (p && p.position) return { position: p.position, normal: p.normal, floorId: p.floorId };
      return null;
    },
    // The hover point from ~lagMs ago — used so a pin lands where the cursor
    // was resting on the defect, not where it drifted while moving to a button.
    getStableHit: (lagMs = 550) => {
      const h = hitHistoryRef.current;
      if (!h.length) return null;
      const target = performance.now() - lagMs;
      let best = h[0];
      for (const e of h) { if (e.t <= target) best = e; else break; }
      return { position: best.position, normal: best.normal, floorId: best.floorId };
    },
    // Resolve with the 3D point under the cursor when the user presses ENTER.
    // We deliberately AVOID click capture: a click makes Matterport navigate,
    // which shifts the intersection off the defect before we can read it.
    // Hovering updates the point without moving the camera, so ENTER-while-
    // hovering gives the exact spot. Requires parent focus (true right after
    // pressing our Start button); Esc cancels.
    captureNextClickHit: (onClickWarn) => new Promise((resolve) => {
      const iframe = iframeRef.current;
      try { window.focus(); if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur?.(); } catch (e) {}
      dbg("capture armed · hover defect + press ENTER");
      let done = false;
      const finish = (hit) => { if (done) return; done = true; cleanup(); resolve(hit); };
      const onKey = (e) => {
        if (e.key === "Enter" || e.key === " " || e.code === "Space") {
          e.preventDefault(); e.stopPropagation();
          const p = lastIntersect.current;
          dbg(`ENTER · hit=${p?.position ? `${p.position.x.toFixed(2)},${p.position.y.toFixed(2)},${p.position.z.toFixed(2)}` : "NONE"}`);
          finish(p && p.position ? { position: p.position, normal: p.normal, floorId: p.floorId } : { noHit: true });
        } else if (e.key === "Escape") { finish({ cancelled: true }); }
      };
      // Detect an accidental click (which navigates the camera) and warn — do NOT pin on it.
      const onBlur = () => { setTimeout(() => { if (document.activeElement === iframe) { dbg("clicked scan → camera moved; use ENTER instead"); try { onClickWarn && onClickWarn(); } catch (e) {} } }, 40); };
      const timer = setTimeout(() => finish({ cancelled: true }), 45000);
      const cleanup = () => { window.removeEventListener("keydown", onKey, true); window.removeEventListener("blur", onBlur); clearTimeout(timer); pinCaptureCancelRef.current = null; };
      pinCaptureCancelRef.current = () => finish({ cancelled: true });
      window.addEventListener("keydown", onKey, true);
      window.addEventListener("blur", onBlur);
    }),
    cancelPinCapture: () => { if (pinCaptureCancelRef.current) pinCaptureCancelRef.current(); },
    // Current tags as Matterport actually holds them (ground-truth positions).
    getTags: () => tagsRef.current.map((t) => ({ sid: t.sid || t.id, anchorPosition: t.anchorPosition })),
    // Place a native Matterport 3D tag anchored to a true world point. Pass
    // position/stemVector to restore an existing pin, or omit to use the
    // current pointer hit for a fresh pin. Returns { sid, position, stemVector, floorId }.
    pinDefect: async ({ label, description, severity, position, stemVector, floorId } = {}) => {
      const sdk = sdkRef.current;
      const ns = sdk?.Tag || sdk?.Mattertag;
      if (!ns?.add) { dbg("pinDefect: NO tag namespace"); return null; }
      let pos = position, stem = stemVector, fid = floorId;
      if (!pos) {
        const h = hitHistoryRef.current;
        let hit = lastIntersect.current;
        if (h.length) {
          const target = performance.now() - 550;
          let best = h[0];
          for (const e of h) { if (e.t <= target) best = e; else break; }
          hit = best;
        }
        if (!hit?.position) return null;
        pos = hit.position;
        const n = hit.normal || { x: 0, y: 1, z: 0 };
        stem = { x: n.x * 0.18, y: n.y * 0.18, z: n.z * 0.18 };
        fid = hit.floorId;
      }
      try {
        const sids = await ns.add({ label: label || "Defect", description: description || "", anchorPosition: pos, stemVector: stem, color: SEV_RGB[severity] || SEV_RGB.medium });
        const sid = Array.isArray(sids) ? sids[0] : sids;
        dbg(`pinDefect: Tag.add OK sid=${sid} at ${pos.x?.toFixed?.(2)},${pos.y?.toFixed?.(2)},${pos.z?.toFixed?.(2)}`);
        return { sid, position: pos, stemVector: stem, floorId: fid };
      } catch (e) { dbg(`pinDefect: Tag.add FAILED ${e?.message || e}`); return null; }
    },
    removePin: async (sid) => {
      const sdk = sdkRef.current;
      const ns = sdk?.Tag || sdk?.Mattertag;
      if (!ns?.remove || !sid) return;
      try { await ns.remove(sid); } catch (e) {}
    },
    // Rebuild all native pins from the given defect list (clears previous set).
    syncPins: async (defects) => {
      const sdk = sdkRef.current;
      const ns = sdk?.Tag || sdk?.Mattertag;
      if (!ns?.add) return;
      const old = Object.values(pinsRef.current);
      for (const s of old) { try { await ns.remove?.(s); } catch (e) {} }
      pinsRef.current = {};
      for (const d of defects || []) {
        const a = d.anchor3d;
        if (!a?.position) continue;
        try {
          const sids = await ns.add({ label: d.title || "Defect", description: d.defect_type || "", anchorPosition: a.position, stemVector: a.stemVector || { x: 0, y: 0.18, z: 0 }, color: SEV_RGB[d.severity] || SEV_RGB.medium });
          pinsRef.current[d.id] = Array.isArray(sids) ? sids[0] : sids;
        } catch (e) {}
      }
    },
    gotoSweep: async (sweepId, aim, lookAtPos) => {
      const sdk = sdkRef.current;
      if (!sdk || !sweepId) return false;
      try {
        if (sdk.Mode?.moveTo && sdk.Mode?.Mode) await sdk.Mode.moveTo(sdk.Mode.Mode.INSIDE);
        await sdk.Sweep.moveTo(sweepId, { transition: sdk.Sweep.Transition?.FLY });
        // Prefer pointing the camera straight at the pin's real 3D coordinate.
        if (lookAtPos && sdk.Camera?.lookAt) {
          try { await sdk.Camera.lookAt(lookAtPos); dbg(`lookAt ${lookAtPos.x?.toFixed?.(2)},${lookAtPos.y?.toFixed?.(2)},${lookAtPos.z?.toFixed?.(2)}`); }
          catch (e) { dbg(`lookAt failed: ${e?.message || e}`); if (aim && sdk.Camera?.setRotation) { try { await sdk.Camera.setRotation({ x: aim.pitch, y: aim.yaw }, { speed: 100 }); } catch (e2) {} } }
        } else if (aim && sdk.Camera?.setRotation) {
          try { await sdk.Camera.setRotation({ x: aim.pitch, y: aim.yaw }, { speed: 100 }); } catch (e) {}
        }
        return true;
      } catch (e) { return false; }
    },
    // Turn the camera to face a world point without changing sweep.
    lookAt: async (worldPos) => {
      const sdk = sdkRef.current;
      if (!sdk?.Camera?.lookAt || !worldPos) return false;
      try { await sdk.Camera.lookAt(worldPos); dbg(`lookAt ${worldPos.x?.toFixed?.(2)},${worldPos.y?.toFixed?.(2)},${worldPos.z?.toFixed?.(2)}`); return true; }
      catch (e) { dbg(`lookAt failed: ${e?.message || e}`); return false; }
    },
    getCameraPose: () => poseRef.current,
    // Project a 3D world point to exact screen pixels using Matterport's own
    // Conversion API (the correct, drift-free way to anchor an overlay marker).
    worldToScreen: (worldPos) => {
      const sdk = sdkRef.current;
      const iframe = iframeRef.current;
      if (!sdk?.Conversion?.worldToScreen || !poseRef.current || !iframe || !worldPos) return null;
      const size = { w: iframe.clientWidth, h: iframe.clientHeight };
      try {
        const s = sdk.Conversion.worldToScreen(worldPos, poseRef.current, size);
        if (!s) return null;
        return { x: s.x, y: s.y, z: s.z, w: size.w, h: size.h };
      } catch (e) { return null; }
    },
    listSweeps: () => sweepsRef.current.map((s) => ({ id: s.id || s.sid, position: s.position })),
    captureSweep: async (sweepId) => {
      const sdk = sdkRef.current;
      if (!sdk) return null;
      try {
        if (sdk.Mode?.moveTo && sdk.Mode?.Mode) await sdk.Mode.moveTo(sdk.Mode.Mode.INSIDE);
        await sdk.Sweep.moveTo(sweepId, { transition: sdk.Sweep.Transition?.INSTANT });
        await new Promise((r) => setTimeout(r, 900));
        const data = await sdk.Renderer.takeEquirectangular();
        return typeof data === "string" ? data : (data?.base64 || null);
      } catch (e) {
        return null;
      }
    },
    toggleMeasure: async () => {
      const sdk = sdkRef.current;
      if (!sdk?.Measurements?.toggleMode) return;
      const next = !measuring;
      try { await sdk.Measurements.toggleMode(next); setMeasuring(next); } catch (e) {}
    },
    getLastMeasurement: () => {
      const list = measRef.current;
      if (!list.length) return null;
      const m = list[list.length - 1];
      const meters = m && m.total != null ? m.total : null;
      if (meters == null) return m?.label || null;
      return fmtMeters(meters, unitsRef.current);
    },
    captureScreenshot: async () => {
      const sdk = sdkRef.current;
      if (!sdk?.Renderer?.takeScreenShot) return null;
      try {
        const uri = await sdk.Renderer.takeScreenShot(
          { width: 1600, height: 900 },
          { mattertags: false, sweeps: false, measurements: false }
        );
        return uri || null;
      } catch (e) { console.error("[Matterport] screenshot failed:", e); return null; }
    },
    // Switch to Matterport's top-down FLOORPLAN view, screenshot it, and project the
    // given world points (defect pins) onto that image via Conversion.worldToScreen.
    // Returns { image, points:[{id,xPct,yPct}], w, h } or null. Live-only (needs the SDK/model).
    captureFloorplan: async (worldPoints = []) => {
      const sdk = sdkRef.current;
      const iframe = iframeRef.current;
      if (!sdk?.Mode?.moveTo || !sdk?.Mode?.Mode || !sdk?.Renderer?.takeScreenShot || !iframe) return null;
      try {
        await sdk.Mode.moveTo(sdk.Mode.Mode.FLOORPLAN);
        await new Promise((r) => setTimeout(r, 1500));
        const size = { w: iframe.clientWidth, h: iframe.clientHeight };
        const uri = await sdk.Renderer.takeScreenShot(
          { width: 1600, height: Math.max(1, Math.round((1600 * size.h) / Math.max(size.w, 1))) },
          { mattertags: false, sweeps: false, measurements: false }
        );
        let pose = poseRef.current;
        try { if (sdk.Camera?.getPose) pose = await sdk.Camera.getPose(); } catch (e) {}
        let placed = 0;
        const points = worldPoints.map((wp) => {
          try {
            const s = sdk.Conversion?.worldToScreen?.(wp.position, pose, size);
            if (s && s.x != null) { placed++; return { id: wp.id, xPct: s.x / size.w, yPct: s.y / size.h }; }
          } catch (e) {}
          return { id: wp.id, xPct: null, yPct: null };
        });
        console.log(`[Matterport] floorplan captured · ${placed}/${worldPoints.length} pins projected · pose:${pose ? "ok" : "null"} · worldToScreen:${sdk.Conversion?.worldToScreen ? "ok" : "missing"}`);
        return { image: uri || null, points, w: size.w, h: size.h };
      } catch (e) {
        console.error("[Matterport] floorplan capture failed:", e);
        return null;
      }
    },
  }));

  useEffect(() => {
    if (!modelId) { setStatus("noscan"); return; }
    if (!sdkKey) { setStatus("error"); return; }
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const connect = await getMpConnect(sdkKey);
        const sdk = await connect(iframe);
        if (cancelled) return;
        sdkRef.current = sdk;
        setStatus("ready");
        loggedHit.current = false;
        dbg(`SDK ready · tag namespace: ${sdk.Tag ? "Tag" : sdk.Mattertag ? "Mattertag" : "NONE"}`);
        sdk.Pointer?.intersection?.subscribe((intersection) => {
          if (intersection && intersection.object !== "intersectedobject.none") {
            lastIntersect.current = intersection;
            if (!loggedHit.current && intersection.position) { loggedHit.current = true; dbg(`pointer OK · obj=${intersection.object}`); }
            const h = hitHistoryRef.current;
            h.push({ t: performance.now(), position: intersection.position, normal: intersection.normal, floorId: intersection.floorId });
            const cutoff = performance.now() - 3000;
            while (h.length && h[0].t < cutoff) h.shift();
          }
        });
        sdk.Camera?.pose?.subscribe((p) => { poseRef.current = p; });
        sdk.Sweep?.data?.subscribe({
          onCollectionUpdated: (coll) => { sweepsRef.current = Object.values(coll || {}); },
        });
        sdk.Measurements?.data?.subscribe({
          onCollectionUpdated: (coll) => { measRef.current = Object.values(coll || {}); },
        });
        const tns = sdk.Tag || sdk.Mattertag;
        tns?.data?.subscribe?.({ onCollectionUpdated: (coll) => { tagsRef.current = Object.values(coll || {}); } });
      } catch (e) {
        if (!cancelled) { console.error("[Matterport] SDK connect failed:", e); setStatus("error"); dbg(`SDK connect FAILED: ${e?.message || e}`); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, sdkKey]);

  const toggleMeasure = async () => {
    const sdk = sdkRef.current;
    if (!sdk?.Measurements?.toggleMode) return;
    const next = !measuring;
    try { await sdk.Measurements.toggleMode(next); setMeasuring(next); } catch (e) {}
  };

  const toggleUnits = () => {
    const next = units === "metric" ? "imperial" : "metric";
    setUnits(next);
    unitsRef.current = next;
  };

  const zoom = async (delta) => {
    const sdk = sdkRef.current;
    if (!sdk?.Camera) return;
    try {
      if (sdk.Mode?.moveTo && sdk.Mode?.Mode) await sdk.Mode.moveTo(sdk.Mode.Mode.INSIDE);
      if (delta === 0) await sdk.Camera.zoomReset();
      else await sdk.Camera.zoomBy(delta);
    } catch (e) { /* not inside / out of range */ }
  };

  const src = modelId
    ? `https://my.matterport.com/show/?m=${modelId}&play=1&qs=1&brand=0&mls=2&title=0&hr=0&hl=2&help=0&mt=0&wh=0&search=0&views=0&gt=0${sdkKey ? `&applicationKey=${sdkKey}` : ""}`
    : "";

  return (
    <div className="relative w-full h-full bg-[#0A0A0A] overflow-hidden" data-testid="matterport-viewer">
      {modelId ? (
        <iframe
          ref={iframeRef}
          title="Matterport scan"
          src={src}
          className="w-full h-full border-0"
          allow="xr-spatial-tracking; fullscreen; vr"
          allowFullScreen
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
          <Box className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="font-display text-lg font-semibold">No Matterport scan linked</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            Add this property's Matterport Model ID in project settings (and your SDK key under
            Settings) to embed the 3D scan. You can still log defects manually below.
          </p>
        </div>
      )}

      {modelId && (
        <>
          {status !== "ready" && (
            <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
              <span
                className="text-[11px] px-2.5 py-1 rounded-full bg-black/70 backdrop-blur border border-white/10 flex items-center gap-1.5"
                data-testid="viewer-status"
              >
                {status === "loading" && <><span className="w-1.5 h-1.5 rounded-full bg-[#FFCC00] animate-pulse" /> Connecting…</>}
                {status === "error" && <><AlertCircle className="w-3 h-3 text-[#FF9500]" /> Scan embedded (SDK limited)</>}
              </span>
            </div>
          )}
          <div className="absolute top-0 left-0 right-0 h-12 z-[6] bg-[#0A0A0A] border-b border-white/5 pointer-events-none" data-testid="mp-top-cover" />
          {(status === "ready" || status === "error") && (
            <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1.5" data-testid="viewer-controls">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={toggleUnits}
                  data-testid="units-toggle"
                  title="Switch measurement units (metric / imperial)"
                  className="text-[11px] px-2.5 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/10 text-white hover:bg-black transition-colors"
                >
                  {units === "metric" ? "Metric (m)" : "Imperial (ft)"}
                </button>
                <button
                  onClick={toggleMeasure}
                  data-testid="measure-toggle"
                  className={`text-[11px] px-2.5 py-1.5 rounded-full backdrop-blur border flex items-center gap-1.5 transition-colors ${measuring ? "bg-[#C5A24A] border-[#C5A24A] text-black" : "bg-black/70 border-white/10 text-white hover:bg-black"}`}
                >
                  <Ruler className="w-3 h-3" /> {measuring ? "Measuring…" : "Measure"}
                </button>
              </div>
              <div className="flex items-center gap-1.5" data-testid="zoom-controls">
                <button onClick={() => zoom(0.3)} data-testid="zoom-in" title="Zoom in" className="w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:bg-black transition-colors"><ZoomIn className="w-4 h-4" /></button>
                <button onClick={() => zoom(-0.3)} data-testid="zoom-out" title="Zoom out" className="w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:bg-black transition-colors"><ZoomOut className="w-4 h-4" /></button>
                <button onClick={() => zoom(0)} data-testid="zoom-reset" title="Reset zoom" className="w-8 h-8 rounded-full bg-black/70 backdrop-blur border border-white/10 text-muted-foreground flex items-center justify-center hover:text-white transition-colors"><RotateCcw className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

MatterportViewer.displayName = "MatterportViewer";
export default MatterportViewer;
